import type { CodecItem, CodecDimMeta } from '../types'

// ─── All 25 CODE-C items ───────────────────────────────────────────────────────
// reverseCoded = true for positions 4 & 5 in each dimension
// These are the canonical items from Clutch_CODE-C_Readiness_Resistance_Assessment_v3

export const CODEC_ITEMS: CodecItem[] = [
  // Curiosity (C)
  { id: 'C1',  dim: 'curiosity',   reverseCoded: false, text: 'I seek out information to better understand this change.' },
  { id: 'C2',  dim: 'curiosity',   reverseCoded: false, text: 'I ask questions when something about the change is unclear.' },
  { id: 'C3',  dim: 'curiosity',   reverseCoded: false, text: 'I explore ways this change could improve results for customers or the public.' },
  { id: 'C4',  dim: 'curiosity',   reverseCoded: true,  text: 'I avoid engaging with information about the change until I have to.' },
  { id: 'C5',  dim: 'curiosity',   reverseCoded: true,  text: 'I have little interest in learning new ways of working related to this change.' },

  // Optimism (O)
  { id: 'O1',  dim: 'optimism',    reverseCoded: false, text: 'I believe this change can lead to meaningful improvements.' },
  { id: 'O2',  dim: 'optimism',    reverseCoded: false, text: 'I believe our organization/team can successfully implement this change.' },
  { id: 'O3',  dim: 'optimism',    reverseCoded: false, text: 'I expect the benefits of this change will outweigh the costs or inconvenience.' },
  { id: 'O4',  dim: 'optimism',    reverseCoded: true,  text: 'I doubt leadership has a realistic plan for making this change work.' },
  { id: 'O5',  dim: 'optimism',    reverseCoded: true,  text: 'I expect this change will create more problems than it solves.' },

  // Drive (D)
  { id: 'D1',  dim: 'drive',       reverseCoded: false, text: 'I am willing to invest effort to make this change successful.' },
  { id: 'D2',  dim: 'drive',       reverseCoded: false, text: 'I prioritize time to adapt my work to align with this change.' },
  { id: 'D3',  dim: 'drive',       reverseCoded: false, text: 'I follow through on change-related actions even when it is inconvenient.' },
  { id: 'D4',  dim: 'drive',       reverseCoded: true,  text: 'I am too overloaded to take on additional change right now.' },
  { id: 'D5',  dim: 'drive',       reverseCoded: true,  text: 'I plan to do only the minimum required for this change.' },

  // Evolve (E)
  { id: 'E1',  dim: 'evolve',      reverseCoded: false, text: 'I feel capable of learning what I need to succeed in this change.' },
  { id: 'E2',  dim: 'evolve',      reverseCoded: false, text: 'I can adapt my approach when new information or requirements emerge.' },
  { id: 'E3',  dim: 'evolve',      reverseCoded: false, text: 'I have access to the training, guidance, or support I need to build capability for this change.' },
  { id: 'E4',  dim: 'evolve',      reverseCoded: true,  text: 'I worry I will look incompetent while learning the new way of working.' },
  { id: 'E5',  dim: 'evolve',      reverseCoded: true,  text: 'This change feels too complex to implement well.' },

  // Connection (CN) — NOTE: code is CN, not C (C = Curiosity)
  { id: 'CN1', dim: 'connection',  reverseCoded: false, text: 'I trust my direct manager/sponsor to support people through this change.' },
  { id: 'CN2', dim: 'connection',  reverseCoded: false, text: 'I feel safe raising concerns or asking for help related to this change.' },
  { id: 'CN3', dim: 'connection',  reverseCoded: false, text: 'People on my team collaborate to solve problems that arise from this change.' },
  { id: 'CN4', dim: 'connection',  reverseCoded: true,  text: 'People around me discourage engagement with this change.' },
  { id: 'CN5', dim: 'connection',  reverseCoded: true,  text: 'I do not believe employee feedback about this change will be heard or acted upon.' },
]

// ─── Dimension metadata ────────────────────────────────────────────────────────

export const CODEC_DIMS: CodecDimMeta[] = [
  {
    key: 'curiosity',
    label: 'Curiosity',
    subtitle: 'Open to learning, seeking information, proactive engagement',
    icon: '🔍',
    resistanceItems: ['C4', 'C5'],
  },
  {
    key: 'optimism',
    label: 'Optimism',
    subtitle: 'Belief the change can work; confidence in outcomes',
    icon: '☀️',
    resistanceItems: ['O4', 'O5'],
  },
  {
    key: 'drive',
    label: 'Drive',
    subtitle: 'Willingness to invest effort and persist through friction',
    icon: '⚡',
    resistanceItems: ['D4', 'D5'],
  },
  {
    key: 'evolve',
    label: 'Evolve',
    subtitle: 'Comfort experimenting, learning orientation, adaptability',
    icon: '🌱',
    resistanceItems: ['E4', 'E5'],
  },
  {
    key: 'connection',
    label: 'Connection',
    subtitle: 'Trust, coordination, inclusion, and support networks',
    icon: '🤝',
    resistanceItems: ['CN4', 'CN5'],
  },
]

// ─── Interpretation content (from CODE-C Interpretation Guide v4) ──────────────

export const CODEC_PATTERNS = [
  {
    title: 'High Readiness + High Resistance',
    interp: 'People understand the change but feel threatened or overloaded by its personal impact. Understanding ≠ willingness.',
    recs: [
      'Increase transparency on personal impact — clarify specifically what changes for whom.',
      'Address workload and fairness concerns directly. Communication alone will not close this gap.',
      'Involve affected employees in solutioning where possible to reduce the sense of things happening to them.',
    ],
  },
  {
    title: 'Low Readiness + High Resistance',
    interp: 'Active opposition or fear. The change lacks legitimacy or feels threatening. Slow down before any rollout activity.',
    recs: [
      'Rebuild the case for change at a personal, role-specific level — not the organizational level.',
      'Address broken trust from prior change experiences before asking for new commitments.',
      'Invest in listening and leader visibility. Town halls and 1:1s with sponsors matter more than communications.',
    ],
  },
  {
    title: 'Low Curiosity',
    interp: 'Communications are not reaching or resonating. Avoidance behavior is present — employees are not seeking information or asking questions. Trust and relevance problem before a knowledge problem.',
    recs: [
      'Redesign communications to be role-specific and concrete, not initiative-level announcements.',
      'Use manager-led conversations and two-way Q&A rather than broadcast messages.',
      'Deploy peer ambassadors to normalize curiosity and reduce social inhibition around asking questions.',
    ],
  },
  {
    title: 'Low Optimism',
    interp: 'Employees doubt the change will succeed or doubt leadership\'s ability to deliver it. Prior failures or visible plan gaps are amplifying cynicism.',
    recs: [
      'Show a credible plan with specific milestones — abstract promises worsen cynicism.',
      'Create early, visible quick wins and announce them clearly.',
      'Increase leader visibility and accountability. This is a behavior change for leaders, not a communications fix.',
    ],
  },
  {
    title: 'Low Drive',
    interp: 'Competing priorities or capacity overload is suppressing willingness. More often a workload problem than a motivation problem.',
    recs: [
      'Clarify what this change replaces — show employees something coming off their plate.',
      'Escalate competing priority conflicts to leadership rather than relying on individual motivation.',
      'Phase the rollout to reduce concurrent demands during the highest-effort adoption window.',
    ],
  },
  {
    title: 'Low Evolve',
    interp: 'Fear of incompetence or complexity overload. Employees may want the change but don\'t believe they can succeed in it. Psychological safety for learning is insufficient.',
    recs: [
      'Design training as a practice environment, not a content delivery event.',
      'Normalize learning curves explicitly at the manager level — model uncertainty from the top.',
      'Build just-in-time support into the workflow — accessible within 2 minutes is more effective than comprehensive training.',
    ],
  },
  {
    title: 'Low Connection',
    interp: 'Trust has eroded or peer norms are working against engagement. Social norms spread faster than any communication.',
    recs: [
      'Strengthen the manager layer first. Connection is built through consistent supervisor behavior, not campaigns.',
      'Create structured peer support — change networks, champions, working groups.',
      'Demonstrate responsiveness to feedback visibly. Showing input changed something is more powerful than listening sessions.',
    ],
  },
]
